/**
 * The filter object, and the URL it is written in (`KLQ-20260821-001` §B).
 *
 * **One object, three readers.** The register, the board's four columns and the CSV export all
 * narrow by the same `TaskFilters`, because the server narrows all three by the same
 * `TaskFilters` — `_filters()` in `backend/src/kalqa/domains/tasks/router.py` builds one
 * predicate for the list handler and the export handler precisely so that *"export the current
 * filter"* is structurally true rather than maintained. A second copy on this side would undo
 * that from the outside.
 *
 * TWIN: `_filters()` in `domains/tasks/router.py` and `TaskFilters` in that domain's
 * `service.py`. A parameter added there is a field here, a `TASK_SEARCH_KEYS` entry, and a
 * line in `taskQuery()` (`./api.ts`). A parameter this file forgets is a filter the person
 * cannot link to; a parameter `api.ts` forgets is a filter the server never hears about.
 *
 * **The URL is the state, not a copy of it.** Nothing here is component state that also
 * happens to be written to the address bar: the search params are read on every render and a
 * change is a navigation. That is what makes a filtered register a link somebody can paste
 * into a meeting invite (§B), and it is why `searchFromFilters` is the exact inverse of
 * `filtersFromSearch` rather than approximately so.
 *
 * **Every field is present.** `null`, `false` or an empty array for absent rather than an
 * optional key: the object is total, so `queries.ts` can put the whole of it in a query key and
 * get a stable hash, and no reader has to remember which of thirteen parameters might be
 * missing.
 *
 * **Four fields hold a set** (`KLQ-20260825-003` §E/§F): `kind`, `status`, `priority` and
 * `assignee` mean *any of these*, and `[]` means the field asks nothing. The members are sorted
 * and de-duplicated the moment the URL is written, in `parseTaskSearch`, so `a,b` and `b,a` are
 * one link, one query key and one cache entry rather than two of each.
 */

import type { TaskKind, TaskPriority, TaskStatus } from "./api";
import { TASK_KINDS, TASK_PRIORITIES, TASK_STATUSES } from "./api";

/**
 * The six built-in saved filters, sent as `saved=` and answered server-side.
 *
 * TWIN: `SAVED_FILTERS` in `domains/tasks/service.py`. They are never re-derived here — the
 * server keys *Mine* and *This week* on the caller and on its own clock, and a client that
 * computed "this week" from a browser's timezone would disagree with the export of the same
 * filter.
 *
 * **Four are about people and two are about firms** (`needs_an_owner` and `my_company`, added
 * 2026-09-13 with `MCK-20260521-008`'s company assignment), and the split is the point.
 * *Unassigned* means nobody owes this at all; *Needs an owner* means a **firm** owes it and
 * nobody there has picked it up. Different problems for different people, and a register that
 * folded them together would hide the second behind the first. *My company* is the team queue
 * for the same reason *Mine* stays personal: folding the firm's work into *Mine* makes *Mine*
 * the thing nobody reads.
 */
export const SAVED_FILTERS = [
  "mine",
  "overdue",
  "unassigned",
  "this_week",
  "needs_an_owner",
  "my_company",
] as const;
export type SavedFilter = (typeof SAVED_FILTERS)[number];

/**
 * Which of the three arrangements is on screen. Part of the URL, because a board is a link
 * too — and so is a plan somebody sliced to one floor.
 *
 * `hierarchy` is the third (`KLQ-20260824-001`, the view `chat1.md` line 221 reserved). It is
 * a *view* rather than a screen for the reason the board is: it narrows by the same filter
 * object, so switching arrangement carries the question with it.
 */
// Order is the toggle's order, and the default leads it (`KLQ-20260825-002` §5) — a chip strip
// whose first entry is not what the bare URL shows reads as a lost selection.
export const TASK_VIEWS = ["hierarchy", "register", "board"] as const;
export type TaskView = (typeof TASK_VIEWS)[number];

/**
 * Everything the register, the board and the export narrow by.
 *
 * `under` is the slicer's parameter (`KLQ-20260820-005`) and sits in the same object as the
 * rest on purpose: §A makes the hierarchy a *filter facet* rather than a second screen, so a
 * subtree is one more predicate and not a different question.
 */
export interface TaskFilters {
  readonly saved: SavedFilter | null;
  /** Any-of: two values ask for the tasks carrying either. `[]` narrows by nothing. */
  readonly kind: readonly TaskKind[];
  readonly status: readonly TaskStatus[];
  readonly priority: readonly TaskPriority[];
  /** Any-of, over user ids. `[]` is *anyone*, which is not the same question as `unassigned`. */
  readonly assignee: readonly string[];
  /**
   * *Assigned to nobody*, as its own flag rather than as a member of `assignee`
   * (`KLQ-20260825-003` §E — the wire names do not change, and `unassigned=true` stays a
   * boolean the server ORs into the assignee predicate, §C). A sentinel inside the id list
   * would make an id list that is not a list of ids, and every reader of it would need to know
   * the exception.
   */
  readonly unassigned: boolean;
  /** ISO instants. The API takes a datetime; the picker gives a day and widens it to one. */
  readonly dueAfter: string | null;
  readonly dueBefore: string | null;
  readonly hasAttachments: boolean | null;
  readonly drawingLinked: boolean | null;
  /** Any-of, as the server reads it: two chips ask for the tasks carrying either. */
  readonly labels: readonly string[];
  /** `false` selects *closed with nothing to show for it* — see `TaskFilters.resolved`. */
  readonly resolved: boolean | null;
  readonly under: readonly string[];
  /**
   * Statutory objects (SO/PS) — any-of, and **about the task's deliverables rather than the
   * task** (`KLQ-20260817-002`; owner 2026-09-11).
   *
   * A task has no object of its own; the drawings it owns do, and one task legitimately
   * produces drawings under two. So this asks *"does this task produce a drawing filed under
   * any of these?"* — which is exactly what the plan's object pivot already means, and they
   * have to mean the same thing or the slice and the grouping would disagree about one word.
   */
  readonly objects: readonly string[];
}

/** The project-wide register: no narrowing at all. */
export const NO_FILTERS: TaskFilters = {
  saved: null,
  kind: [],
  status: [],
  priority: [],
  objects: [],
  assignee: [],
  unassigned: false,
  dueAfter: null,
  dueBefore: null,
  hasAttachments: null,
  drawingLinked: null,
  labels: [],
  resolved: null,
  under: [],
};

/**
 * The URL form: flat, string-valued, and readable by a person typing one by hand.
 *
 * `kind`, `status`, `priority`, `assignee`, `labels` and `under` are comma-joined rather
 * than repeated, which is the call `app/router.tsx` already made for the drawings slicer:
 * `?under=a,b` is the shape somebody can write into a chat message, and the repeated form
 * the API wants is produced once, in `api.ts`, where every other query-shape decision lives.
 */
export interface TaskSearch {
  readonly view?: TaskView;
  readonly saved?: SavedFilter;
  readonly kind?: string;
  readonly status?: string;
  readonly priority?: string;
  readonly assignee?: string;
  /** Statutory objects, comma-joined. `?object=` and not `?object_id=` — the URL is read by
      people, and the wire name is produced in `api.ts` with every other query-shape decision. */
  readonly object?: string;
  readonly unassigned?: boolean;
  readonly due_after?: string;
  readonly due_before?: string;
  readonly attachments?: boolean;
  readonly drawing?: boolean;
  readonly labels?: string;
  readonly resolved?: boolean;
  readonly under?: string;
  /**
   * The project's phase context (`KLQ-20260827-002`).
   *
   * **Not a member of `TaskFilters`, deliberately.** Everything else in this object is a
   * question the tasks screen asks; the phase is which stage of the project every screen is
   * asking *about*, it is owned by the chrome above the tab row, and it travels between tabs.
   * Putting it in the filter object would make it something a filter control could clear —
   * and clearing it from the tasks tab would silently change the drawings tab too. It is
   * parsed and preserved here so the route does not drop it; `useTaskFilterNavigation` carries
   * it across a filter change.
   */
  readonly phase?: string;
}

function oneOf<T extends string>(
  allowed: readonly T[],
  value: unknown,
): T | null {
  return typeof value === "string" &&
    (allowed as readonly string[]).includes(value)
    ? (value as T)
    : null;
}

/** `?attachments=true`. Anything that is not one of the two words is not an answer. */
function boolean(value: unknown): boolean | null {
  if (value === true || value === "true") return true;
  if (value === false || value === "false") return false;
  return null;
}

/** An id we will hand back to the server. Not parsed, only refused when it is empty. */
function id(value: unknown): string | null {
  return typeof value === "string" && value !== "" ? value : null;
}

/** An instant. A string that is not a date is dropped rather than sent for the API to refuse. */
function instant(value: unknown): string | null {
  if (typeof value !== "string" || value === "") return null;
  return Number.isNaN(Date.parse(value)) ? null : value;
}

function idList(value: unknown): readonly string[] {
  if (typeof value !== "string" || value === "") return [];
  return value.split(",").filter((part) => part !== "");
}

/** A comma-separated list of enum values. Unrecognised parts are dropped, not defaulted. */
function enumList<T extends string>(
  allowed: readonly T[],
  value: unknown,
): readonly T[] {
  if (typeof value !== "string" || value === "") return [];
  return value
    .split(",")
    .filter((part): part is T => (allowed as readonly string[]).includes(part));
}

/**
 * One set's members, in one order (`KLQ-20260825-003` §E).
 *
 * **Not inside `idList`.** `under` also flows through that helper and is *index-addressed* by
 * its callers — the slicer's rail reads `under[0]` — so sorting there would silently reorder a
 * selection somebody made. This is applied where a field is known to be a set, and nowhere else.
 */
function sortedSet<T extends string>(values: readonly T[]): readonly T[] {
  return [...new Set(values)].sort();
}

/**
 * `validateSearch` for the tasks route: an arbitrary query string, narrowed to what this
 * screen understands.
 *
 * A parameter that fails to validate is **dropped, not defaulted** — a `?status=urgent` typed
 * by hand should show the whole register rather than silently pick a status nobody asked for.
 * `exactOptionalPropertyTypes` is on, so absent means an absent key, hence the conditional
 * spreads.
 */
/**
 * Which columns the board draws for a status set (`KLQ-20260825-003` §H).
 *
 * **Extracted out of `TaskBoard` so it can be tested**, and the extraction is the point rather
 * than tidiness: the criterion is *"with `status=open,done` the board renders two columns; with
 * no status set, four"*, and while the expression lived inside the component the only way to
 * check it was to mount one. As a function it is three lines of pure logic that vitest reads
 * directly (ADR-0015).
 *
 * A board drawing four columns while the filter says two is a board lying about the question it
 * answers, and greying the excluded two spends a third of the width on nothing.
 *
 * **The order is the workflow's, never the URL's.** `TASK_STATUSES` is the sequence a task
 * actually moves through, so a board built from `?status=done,open` still puts `open` on the
 * left — it does not reshuffle because somebody typed the members the other way round.
 */
export function boardColumns(status: readonly TaskStatus[]): readonly TaskStatus[] {
  if (status.length === 0) return TASK_STATUSES;
  return TASK_STATUSES.filter((candidate) => status.includes(candidate));
}


export function parseTaskSearch(raw: Record<string, unknown>): TaskSearch {
  const view = oneOf(TASK_VIEWS, raw.view);
  const saved = oneOf(SAVED_FILTERS, raw.saved);
  // Sorted and de-duplicated on the way *in*, which is the way out too: `searchFromFilters`
  // writes the URL through this function, so `?status=open,open` and `?status=in_progress,open`
  // each have exactly one written form and the query key that hashes them has one entry.
  const kind = sortedSet(enumList(TASK_KINDS, raw.kind));
  const status = sortedSet(enumList(TASK_STATUSES, raw.status));
  const priority = sortedSet(enumList(TASK_PRIORITIES, raw.priority));
  const assignee = sortedSet(idList(raw.assignee));
  const unassigned = boolean(raw.unassigned);
  const dueAfter = instant(raw.due_after);
  const dueBefore = instant(raw.due_before);
  const attachments = boolean(raw.attachments);
  const drawing = boolean(raw.drawing);
  const labels = idList(raw.labels);
  const resolved = boolean(raw.resolved);
  const under = idList(raw.under);
  const objects = sortedSet(idList(raw.object));
  const phase = id(raw.phase);

  return {
    // `hierarchy` is the default and is never written: the bare project URL is the plan
    // (`KLQ-20260825-002` §5), and a `?view=hierarchy` in every pasted link would be noise.
    // The other two arrangements are written, because for them the parameter is the question.
    ...(view === null || view === "hierarchy" ? {} : { view }),
    ...(saved === null ? {} : { saved }),
    ...(kind.length === 0 ? {} : { kind: kind.join(",") }),
    ...(status.length === 0 ? {} : { status: status.join(",") }),
    ...(priority.length === 0 ? {} : { priority: priority.join(",") }),
    ...(assignee.length === 0 ? {} : { assignee: assignee.join(",") }),
    // `false` is the absent flag, and writing it would put a parameter that changes nothing
    // into every link.
    ...(unassigned === true ? { unassigned } : {}),
    ...(dueAfter === null ? {} : { due_after: dueAfter }),
    ...(dueBefore === null ? {} : { due_before: dueBefore }),
    ...(attachments === null ? {} : { attachments }),
    ...(drawing === null ? {} : { drawing }),
    ...(labels.length === 0 ? {} : { labels: labels.join(",") }),
    ...(resolved === null ? {} : { resolved }),
    ...(objects.length === 0 ? {} : { object: objects.join(",") }),
    ...(under.length === 0 ? {} : { under: under.join(",") }),
    ...(phase === null ? {} : { phase }),
  };
}

/** The URL, as the object every list reads. */
export function filtersFromSearch(search: TaskSearch): TaskFilters {
  return {
    saved: search.saved ?? null,
    kind: enumList(TASK_KINDS, search.kind),
    status: enumList(TASK_STATUSES, search.status),
    priority: enumList(TASK_PRIORITIES, search.priority),
    objects: idList(search.object),
    // No normalisation here: every `TaskSearch` this reads has been through `parseTaskSearch`
    // (the route's `validateSearch`, `app/router.tsx`), which already sorted and de-duplicated.
    assignee: idList(search.assignee),
    unassigned: search.unassigned ?? false,
    dueAfter: search.due_after ?? null,
    dueBefore: search.due_before ?? null,
    hasAttachments: search.attachments ?? null,
    drawingLinked: search.drawing ?? null,
    labels: idList(search.labels),
    resolved: search.resolved ?? null,
    under: idList(search.under),
  };
}

/**
 * The object, as the URL to navigate to. The inverse of `filtersFromSearch`, and the only
 * writer — a filter control changes this object and navigates; it never edits a parameter.
 */
export function searchFromFilters(
  filters: TaskFilters,
  view: TaskView,
): TaskSearch {
  return parseTaskSearch({
    view,
    saved: filters.saved,
    kind: filters.kind.join(","),
    status: filters.status.join(","),
    priority: filters.priority.join(","),
    assignee: filters.assignee.join(","),
    unassigned: filters.unassigned,
    due_after: filters.dueAfter,
    due_before: filters.dueBefore,
    attachments: filters.hasAttachments,
    drawing: filters.drawingLinked,
    labels: filters.labels.join(","),
    resolved: filters.resolved,
    under: filters.under.join(","),
    object: filters.objects.join(","),
  });
}

/**
 * The due-range control speaks days; the API speaks instants. This is the only translation.
 *
 * **The reader's own zone, deliberately.** *"Due until Friday"* means the end of the reader's
 * Friday, not of UTC's, and a range built in UTC would drop a task due on Friday evening for
 * anybody east of it. The URL still carries the instant, so a link opened in another zone is
 * about the same moment rather than the same wall-clock day — the range was a person's, and
 * moving it with the reader would silently change what a shared filter means.
 *
 * `from` is inclusive of its day and `until` is inclusive too, which is why the instant it
 * produces is the *next* day's midnight: the server compares `due_at < due_before`.
 */
function pad(value: number): string {
  return value < 10 ? `0${String(value)}` : String(value);
}

function dayOf(at: Date): string {
  return `${String(at.getFullYear())}-${pad(at.getMonth() + 1)}-${pad(at.getDate())}`;
}

function midnight(day: string, plusDays: number): string | null {
  const parts = /^(\d{4})-(\d{2})-(\d{2})$/.exec(day);
  if (parts === null) return null;
  const at = new Date(
    Number(parts[1]),
    Number(parts[2]) - 1,
    Number(parts[3]) + plusDays,
    0,
    0,
    0,
    0,
  );
  return Number.isNaN(at.getTime()) ? null : at.toISOString();
}

/** The `<input type="date">` value for `dueAfter`. */
export function dayOfDueFrom(instant: string | null): string {
  if (instant === null) return "";
  const at = new Date(instant);
  return Number.isNaN(at.getTime()) ? "" : dayOf(at);
}

/** The same for `dueBefore`, which sits one millisecond into the day *after* the one shown. */
export function dayOfDueUntil(instant: string | null): string {
  if (instant === null) return "";
  const at = new Date(instant);
  return Number.isNaN(at.getTime()) ? "" : dayOf(new Date(at.getTime() - 1));
}

/** `dueAfter` from a picked day: that day's first moment. */
export function dueFromDay(day: string): string | null {
  return day === "" ? null : midnight(day, 0);
}

/** `dueBefore` from a picked day: the first moment after it, so the day itself is included. */
export function dueUntilDay(day: string): string | null {
  return day === "" ? null : midnight(day, 1);
}

/**
 * A task's own `due_at` from a picked day: the **end** of that day, in the picker's zone.
 *
 * Not `dueUntilDay`'s next-midnight — that instant belongs to the following day, and a task
 * due "Friday" that rendered as Saturday would be wrong in exactly the direction a deadline
 * cannot afford. 23:59 rather than 00:00 for the same reason on the other edge: a due date is
 * a day a person promised, and its instant is the last moment the promise still holds.
 */
export function dueAtEndOfDay(day: string): string | null {
  const parts = /^(\d{4})-(\d{2})-(\d{2})$/.exec(day);
  if (parts === null) return null;
  const at = new Date(
    Number(parts[1]),
    Number(parts[2]) - 1,
    Number(parts[3]),
    23,
    59,
    0,
    0,
  );
  return Number.isNaN(at.getTime()) ? null : at.toISOString();
}

/** The `<input type="date">` value for a task's `due_at` — the day the instant falls in. */
export function dayOfDueAt(instant: string | null): string {
  if (instant === null) return "";
  const at = new Date(instant);
  return Number.isNaN(at.getTime()) ? "" : dayOf(at);
}

/**
 * Which view the URL is asking for.
 *
 * `hierarchy` when it does not say (`KLQ-20260825-002` §5). `KLQ-20260821-001` §A made the
 * register the default on a real argument — a flat paginated list is honest about how much of
 * the answer it holds, and a grouped one is not — and that argument stopped being decisive
 * once the plan learned to state its own partiality (§D's `+`, the *loaded so far* line). What
 * is left is which arrangement answers the question people actually arrive with, and that is
 * *what is planned where*.
 *
 * TWIN: the omission rule in `parseTaskSearch`. One default, expressed twice — flipping one
 * without the other makes the toggle write a parameter nothing reads back.
 */
export function viewFromSearch(search: TaskSearch): TaskView {
  return search.view ?? "hierarchy";
}

/**
 * Whether anything is narrowing the register — what the empty state branches on (§C7: a filter
 * that matches nothing offers *Clear filters*, not the hero block a project with no tasks gets).
 */
export function isNarrowed(filters: TaskFilters): boolean {
  return isSliced(filters) || isFieldFiltered(filters);
}

/**
 * Is the **rail** narrowing this list?
 *
 * `under=` is the slicer's parameter and no control in the filter bar writes it any more
 * (`KLQ-20260825-002` §5 moved the rail above all three arrangements and deleted the bar's
 * Location `<select>`). It is split out because the two are answered in different places and a
 * control must never offer to undo something it does not show.
 */
export function isSliced(filters: TaskFilters): boolean {
  return filters.under.length > 0;
}

/**
 * Is anything **in the bar** narrowing this list? Everything except the slice.
 *
 * The assignee set and `unassigned` count here rather than with the slice: the bar has its own
 * controls for both, so the bar can honestly offer to clear them. Location has no such control
 * — the rail owns it (`TasksPage.tsx`, rule 4) — which is the whole distinction.
 */
export function isFieldFiltered(filters: TaskFilters): boolean {
  return (
    filters.saved !== null ||
    filters.kind.length > 0 ||
    filters.status.length > 0 ||
    filters.priority.length > 0 ||
    filters.assignee.length > 0 ||
    filters.unassigned ||
    filters.dueAfter !== null ||
    filters.dueBefore !== null ||
    filters.hasAttachments !== null ||
    filters.drawingLinked !== null ||
    filters.labels.length > 0 ||
    filters.resolved !== null
  );
}

/**
 * One narrowing currently applied, per value, as the bar's chip row draws it
 * (`KLQ-20260910-001` §B).
 *
 * **`field` is the question, `value` is one answer, and the difference is the whole point.**
 * *Open or In progress* is one narrowing with two answers — that is what `KLQ-20260825-003`
 * bought — so it produces two entries carrying the same `field`, and the trigger's count,
 * which counts distinct fields, says 1. A count that said 2 would be telling somebody their
 * question got narrower when what it did was get wider.
 *
 * **`saved` and `under` are not here.** The saved filters have their own chips beside the
 * trigger and are one question with four answers plus none; the slice belongs to the rail,
 * which draws it, and a chip that undid something invisible is a chip people learn not to
 * press. This is the same boundary `withFieldsCleared` draws.
 *
 * **`unassigned` is a value of the `assignee` field**, not a field of its own — the server ORs
 * it into the same predicate and the picker holds it as a chip like a person, so the count must
 * agree with both (§D).
 *
 * Pure, and the reason it lives here rather than in the bar: it is the one thing in this
 * feature's filter UI a vitest can hold (`filters.test.ts`, ADR-0015).
 */
export interface Narrowing {
  /** Which question is narrowed. Two entries sharing it are two answers to one question. */
  readonly field: keyof TaskFilters;
  /** One answer. The enum member, the user id, the label id, or the day for a date bound. */
  readonly value: string;
}

export function narrowings(filters: TaskFilters): readonly Narrowing[] {
  const out: Narrowing[] = [];
  const each = (field: keyof TaskFilters, values: readonly string[]): void => {
    for (const value of values) out.push({ field, value });
  };

  each("kind", filters.kind);
  each("status", filters.status);
  each("priority", filters.priority);
  each("assignee", filters.assignee);
  // Beside the people, under their field. §D: one question, three answers.
  if (filters.unassigned) out.push({ field: "assignee", value: UNASSIGNED });
  each("labels", filters.labels);
  if (filters.dueAfter !== null) {
    out.push({ field: "dueAfter", value: dayOfDueFrom(filters.dueAfter) });
  }
  if (filters.dueBefore !== null) {
    out.push({ field: "dueBefore", value: dayOfDueUntil(filters.dueBefore) });
  }
  for (const field of ["hasAttachments", "drawingLinked", "resolved"] as const) {
    const held = filters[field];
    if (held !== null) out.push({ field, value: held ? "true" : "false" });
  }
  return out;
}

/**
 * The value that stands for *nobody* inside the assignee set (§D).
 *
 * A sentinel in the *control*, never on the wire: `searchFromFilters` splits it back out into
 * `unassigned=true`, and `filtersFromSearch` puts it back. It is not a uuid and cannot collide
 * with one — a user id is 36 characters of hex and hyphens, and this is not.
 */
export const UNASSIGNED = "@unassigned";

/**
 * The filter object after one narrowing is taken off — the chip's cross, in one place.
 *
 * A per-field `onRemove` written at each of eleven call sites is eleven chances to clear the
 * wrong field, and the two that are not simple removals (a date bound goes to `null`, not out
 * of a list; *nobody* is a flag, not a member) are exactly the two somebody would get wrong.
 */
export function withoutNarrowing(
  filters: TaskFilters,
  narrowing: Narrowing,
): TaskFilters {
  const { field, value } = narrowing;
  if (field === "assignee") {
    return value === UNASSIGNED
      ? { ...filters, unassigned: false }
      : { ...filters, assignee: filters.assignee.filter((id) => id !== value) };
  }
  if (field === "kind" || field === "status" || field === "priority" || field === "labels") {
    return { ...filters, [field]: (filters[field] as readonly string[]).filter((one) => one !== value) };
  }
  if (field === "dueAfter" || field === "dueBefore") {
    return { ...filters, [field]: null };
  }
  return { ...filters, [field]: null };
}

/**
 * Everything the bar can clear, cleared — **and the slice left alone**.
 *
 * A *Clear filters* that also dropped the slice would throw away the answer to a question the
 * person asked somewhere else on the screen and can still see selected in the rail. Widening a
 * view somebody did not ask to widen is the same class of surprise as narrowing one.
 */
export function withFieldsCleared(filters: TaskFilters): TaskFilters {
  return { ...NO_FILTERS, under: filters.under };
}

/**
 * The root `/tasks` page's filter (`MCK-20260521-009`) — *what do I owe*, across every project.
 *
 * **A subset of `TaskFilters`, not a copy of it.** `GET /api/v1/tasks` declares five parameters
 * and refuses the rest with `422 filter_not_allowed` (`Surface("my-tasks")`, `DeclaredQueryOnly`):
 * `under`, labels, objects, assignees and the saved filters are all a *project's* vocabulary, and
 * this surface has no project. So the object holds exactly what the server will hear, and a field
 * added to the register cannot leak into this request by being spread in.
 *
 * TWIN: the `my-tasks` descriptor and `list_my_tasks` in `backend/src/kalqa/domains/tasks/`.
 */
export interface MyTaskFilters {
  readonly kind: readonly TaskKind[];
  readonly status: readonly TaskStatus[];
  readonly resolved: boolean | null;
  readonly dueAfter: string | null;
  readonly dueBefore: string | null;
  /**
   * Whose work the page is showing (owner, 2026-09-16). Empty means *mine*, which is what the
   * page answered before this existed and what it answers for everybody who does not run a
   * studio. The server refuses anything else from them — this is a question, never a door.
   */
  readonly people: readonly string[];
  /** The whole studio, including work nobody has taken. Admin and manager only, server-side. */
  readonly everyone: boolean;
}

/**
 * The page's default question: work that is still owed. *Done* and *closed* are answers, not
 * obligations, and a page that opened on them would bury the one row somebody came for under
 * everything they already finished.
 */
export const MY_TASKS_DEFAULT_STATUS: readonly TaskStatus[] = ["in_progress", "open"];

export const MY_TASKS_DEFAULT: MyTaskFilters = {
  kind: [],
  status: MY_TASKS_DEFAULT_STATUS,
  resolved: null,
  dueAfter: null,
  dueBefore: null,
  people: [],
  everyone: false,
};

/**
 * The word the URL uses for *every status*.
 *
 * Needed because the default is not "nothing": the bare `/tasks` asks for open and in progress,
 * so the absence of `?status=` cannot also mean "any status". Removing the last status chip
 * writes `?status=all`, and the round trip stays exact.
 */
export const ALL_STATUSES = "all";

/** `/tasks`'s search string. Same comma-joined, read-by-people shape as `TaskSearch`. */
export interface MyTaskSearch {
  readonly kind?: string;
  readonly status?: string;
  readonly resolved?: boolean;
  readonly due_after?: string;
  readonly due_before?: string;
  /** Comma-joined user ids — `?who=`, not `?assignee=`: the page asks *whose work is this*. */
  readonly who?: string;
  readonly everyone?: boolean;
}

function sameSet<T extends string>(a: readonly T[], b: readonly T[]): boolean {
  return a.length === b.length && a.every((value) => b.includes(value));
}

/**
 * `validateSearch` for `/tasks`. Unrecognised values are dropped, never defaulted — the
 * register's rule, for the register's reason.
 *
 * The default status set is **never written**: the bare path is the page's own question, and a
 * `?status=in_progress,open` on every link somebody pastes would be noise.
 */
export function parseMyTaskSearch(raw: Record<string, unknown>): MyTaskSearch {
  const kind = sortedSet(enumList(TASK_KINDS, raw.kind));
  const everyStatus = raw.status === ALL_STATUSES;
  const status = sortedSet(enumList(TASK_STATUSES, raw.status));
  const resolved = boolean(raw.resolved);
  const dueAfter = instant(raw.due_after);
  const dueBefore = instant(raw.due_before);
  // Ids are not an enum, so they are taken as written and de-duplicated. A stale id is the
  // server's to refuse, and dropping one here would silently change the question.
  const who = [
    ...new Set(
      (typeof raw.who === "string" ? raw.who : "")
        .split(",")
        .map((one) => one.trim())
        .filter((one) => one !== ""),
    ),
  ].sort();
  const everyone = boolean(raw.everyone) === true;
  return {
    ...(kind.length === 0 ? {} : { kind: kind.join(",") }),
    ...(everyStatus
      ? { status: ALL_STATUSES }
      : status.length === 0 || sameSet(status, MY_TASKS_DEFAULT_STATUS)
        ? {}
        : { status: status.join(",") }),
    ...(resolved === null ? {} : { resolved }),
    ...(dueAfter === null ? {} : { due_after: dueAfter }),
    ...(dueBefore === null ? {} : { due_before: dueBefore }),
    // `everyone` wins and the names go: the two say different things, and a URL carrying both
    // would ask one question and show another after a reload.
    ...(everyone ? { everyone: true } : who.length === 0 ? {} : { who: who.join(",") }),
  };
}

/** The URL, as the object the page reads. */
export function myFiltersFromSearch(search: MyTaskSearch): MyTaskFilters {
  return {
    kind: enumList(TASK_KINDS, search.kind),
    status:
      search.status === undefined
        ? MY_TASKS_DEFAULT_STATUS
        : search.status === ALL_STATUSES
          ? []
          : enumList(TASK_STATUSES, search.status),
    resolved: search.resolved ?? null,
    dueAfter: search.due_after ?? null,
    dueBefore: search.due_before ?? null,
    people:
      search.who === undefined ? [] : search.who.split(",").filter((one) => one !== ""),
    everyone: search.everyone ?? false,
  };
}

/** The object, as the URL to navigate to. The exact inverse of `myFiltersFromSearch`. */
export function searchFromMyFilters(filters: MyTaskFilters): MyTaskSearch {
  return parseMyTaskSearch({
    kind: filters.kind.join(","),
    status: filters.status.length === 0 ? ALL_STATUSES : filters.status.join(","),
    resolved: filters.resolved,
    due_after: filters.dueAfter,
    due_before: filters.dueBefore,
    who: filters.people.join(","),
    everyone: filters.everyone,
  });
}

/** Whether the page is asking anything other than its default question. */
export function isMyTasksNarrowed(filters: MyTaskFilters): boolean {
  return (
    filters.kind.length > 0 ||
    !sameSet(filters.status, MY_TASKS_DEFAULT_STATUS) ||
    filters.resolved !== null ||
    filters.dueAfter !== null ||
    filters.dueBefore !== null ||
    // Whose work is a narrowing too — an empty studio-wide answer must not read as
    // *"you have nothing to do"*.
    filters.people.length > 0 ||
    filters.everyone
  );
}
